import { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useUpsertActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useUpsertActionPlan";
import { getAllActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { getOneActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { getAllActionPlanActionsKey } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import {
    hasActionPlanForPost,
    ActionPlanAlreadyExistsError,
    useActionPlanEstimate,
    useActionPlanInvitation,
    useAdoptTemplatePlan,
} from "src/api/client-api/ActionPlan/useActionPlanInvitation";
import {
    classifyTemplateUseFailure,
    isCreationOutcomeUncertain,
} from "src/api/client-api/ActionPlan/templateUseFailure";
import { PostList } from "src/api/client-api/post/types/postList.types";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useActionPlanEligibleScores } from "src/hooks/useActionPlanEligibleScores";
import { isCampaignBefore } from "src/context/Campaigns/useCampaigns";
import { ScoreService } from "src/services/ScoreService";
import { URLHandler } from "src/services/URLHandler";
import type { InvitationNotice } from "../ActionPlanInvitation/TemplateUseAlerts";
import { ActionPlanCreationView, CreationState } from "./ActionPlanCreationView";

type Props = {
    posts: PostList[];
    onClose: () => void;
};

export const ActionPlanCreationDialog = (props: Props) => {
    const { posts, onClose } = props;
    const { company, ssqVersion } = useCompanyMetadata();
    const { entitySsq } = useEntityScores();
    const { campaigns } = useFoodpilotContext();
    const { applicationParameters } = useApplicationParameters();
    const { t } = useTranslation();
    const client = useQueryClient();
    const navigate = useNavigate();
    const create = useUpsertActionPlan();
    const adoption = useAdoptTemplatePlan();
    const [selectedPostId, setSelectedPostId] = useState<number | null>(null);
    const [lastAttempt, setLastAttempt] = useState<"adopt" | "create-own">();
    const [notice, setNotice] = useState<InvitationNotice>();
    const [isFinishingCreation, setIsFinishingCreation] = useState(false);
    const [isVerifyingCreation, setIsVerifyingCreation] = useState(false);
    const creationInFlight = useRef(false);

    const target = campaigns.find(
        (campaign) => campaign["@id"] === applicationParameters?.["actionPlan-targetCampaign"],
    );
    const eligiblePosts = posts
        .filter(
            (post) =>
                post.isLocked &&
                post.campaign &&
                (!target ||
                    isCampaignBefore({
                        startDate: post.campaign.startDate,
                        endDate: target.startDate,
                        canBeEqual: false,
                    })),
        )
        .sort((first, second) => (first.campaign?.startDate ?? "").localeCompare(second.campaign?.startDate ?? ""));
    const selectedPost = eligiblePosts.find((post) => post.id === selectedPostId);
    const invitation = useActionPlanInvitation(selectedPost?.id ?? 0, !!selectedPost && !!target);
    const candidateTemplateId = invitation.data?.templateId;
    const estimate = useActionPlanEstimate(
        selectedPost?.id ?? 0,
        candidateTemplateId,
        target?.id,
        invitation.data?.eligible === true && !!candidateTemplateId && !!target,
    );
    const templateVersion = estimate.isFetching || estimate.isError ? undefined : estimate.data?.templateVersion;
    const estimateFailure = classifyTemplateUseFailure(estimate.error);
    const state: CreationState =
        !selectedPost ? "choose"
        : !target || invitation.isError || invitation.data?.eligible === false ? "unavailable"
        : !invitation.data ? "checking"
        : !candidateTemplateId || estimateFailure.kind === "unavailable" || notice === "unavailable" ? "empty"
        : "proposed";
    const scoreIds = entitySsq ? ScoreService.getScoreIdsFromSsq(entitySsq, []) : [];
    const scores = useActionPlanEligibleScores().filter((score) => scoreIds.includes(score.id));
    const ssqId = selectedPost?.ssqId ?? ssqVersion?.ssq.id;
    const busy = create.isLoading || adoption.isLoading || isFinishingCreation || isVerifyingCreation;
    const creationError = lastAttempt === "adopt" ? adoption.error : create.error;
    const failed = !busy && !!lastAttempt && (lastAttempt === "adopt" ? adoption.isError : create.isError);

    const onCreationSuccess = async () => {
        setIsFinishingCreation(true);
        try {
            await Promise.allSettled([
                client.invalidateQueries([getAllActionPlanKey]),
                client.invalidateQueries([getOneActionPlanKey]),
                client.invalidateQueries([getAllActionPlanActionsKey]),
            ]);
            onClose();

            if (ssqId) {
                navigate(URLHandler.company.actions.planning(company.id, ssqId));
            }
        } finally {
            setIsFinishingCreation(false);
        }
    };

    const verifyUncertainCreation = async (error: unknown, postId: number) => {
        if (!isCreationOutcomeUncertain(error)) return;

        setIsVerifyingCreation(true);
        try {
            if (await hasActionPlanForPost(postId)) {
                await onCreationSuccess();
            }
        } catch {
            // A failed verification leaves the creation result uncertain.
        } finally {
            setIsVerifyingCreation(false);
        }
    };

    const handleCreateOwn = () => {
        if (!selectedPost || !target || !ssqId || invitation.data?.eligible === false || creationInFlight.current)
            return;

        creationInFlight.current = true;
        setLastAttempt("create-own");
        setNotice(undefined);
        void create
            .mutateAsync({
                payload: {
                    name: t("action_plan.trajectory.form.name", "Name"),
                    description: null,
                    simulationVaryPercentage: null,
                    startSsqPost: `/api/ssq-posts/${selectedPost.id}`,
                    endCampaign: target["@id"] ?? `/api/campaigns/${target.id}`,
                    actionPlanScoreTargets: [],
                },
            })
            .then(onCreationSuccess)
            .catch((error) => verifyUncertainCreation(error, selectedPost.id))
            .finally(() => {
                creationInFlight.current = false;
            });
    };

    const handleAdoptionFailure = async (error: unknown, postId: number) => {
        const failure = classifyTemplateUseFailure(error);

        switch (failure.kind) {
            case "plan_already_exists":
                await onCreationSuccess();
                return;
            case "template_changed":
                setNotice("template_changed");
                await estimate.refetch();
                return;
            case "unavailable":
            case "target_year_not_after_diagnostic":
                setNotice(failure.kind);
                return;
            default:
                await verifyUncertainCreation(error, postId);
        }
    };

    const handleAdopt = () => {
        if (
            !selectedPost ||
            !target ||
            !candidateTemplateId ||
            templateVersion === undefined ||
            state !== "proposed" ||
            creationInFlight.current
        )
            return;

        creationInFlight.current = true;
        setLastAttempt("adopt");
        setNotice(undefined);
        void adoption
            .mutateAsync({
                postId: selectedPost.id,
                input: {
                    templateId: candidateTemplateId,
                    endCampaignId: target.id,
                    templateVersion,
                    name: t("action_plan.invitation.default_plan_name", "Action plan"),
                },
            })
            .then(onCreationSuccess)
            .catch((error) => handleAdoptionFailure(error, selectedPost.id))
            .finally(() => {
                creationInFlight.current = false;
            });
    };

    return (
        <ActionPlanCreationView
            posts={eligiblePosts}
            selectedPost={selectedPost}
            onSelectPost={(post) => {
                setSelectedPostId(post?.id ?? null);
                setLastAttempt(undefined);
            }}
            targetYear={target?.year}
            state={state}
            scores={scores}
            projection={
                estimate.isError ?
                    estimate.error instanceof ActionPlanAlreadyExistsError ?
                        { status: "conflict" }
                    :   {
                            status: "error",
                            ...(estimateFailure.kind === "throttled" || estimateFailure.kind === "outage" ?
                                { reason: estimateFailure.kind }
                            :   {}),
                        }

                : estimate.isFetching ?
                    { status: "loading" }
                : estimate.data?.allActionsDropped ?
                    { status: "all-dropped" }
                : estimate.data ?
                    { status: "success", values: estimate.data.values }
                :   { status: "loading" }
            }
            busy={busy}
            creationType={lastAttempt}
            canCreateOwn={!!selectedPost && !!target && invitation.data?.eligible !== false}
            failed={failed}
            failureUncertain={isCreationOutcomeUncertain(creationError)}
            adoptDisabled={templateVersion === undefined}
            notice={notice}
            onClose={onClose}
            onCreateOwn={handleCreateOwn}
            onAdopt={handleAdopt}
            onRetry={() => {
                void invitation.refetch();
                if (estimate.isError) void estimate.refetch();
            }}
        />
    );
};
